import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CaretDown, CaretUp, Trash } from '@phosphor-icons/react';
import clsx from 'clsx';
import { NumberInput } from '@la/ds-ui-components';
import { formatAsUSD } from '@la/utilities';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { makeSelectUserSelectionValuesByProduct } from 'redux/selectors/registrationAddOnsSelectors';
import { AddOnProduct } from '../MemberRegistration.types';
import styles from './MiniCart.module.scss';
import { useMiniCart } from './useMiniCart';

export default function MiniCart() {
  const [isCartExpanded, setIsCartExpanded] = useState(false);
  const miniCartStyle = useMemo(
    () => clsx(styles.miniCart, isCartExpanded && styles.expanded),
    [isCartExpanded]
  );

  const {
    registrationTypeCost,
    registrationTypeLabel,
    selectedProducts,
    totalCost,
  } = useMiniCart();

  const caretDisplay = useMemo(() => {
    if (isCartExpanded) {
      return <CaretUp size={18} weight="fill" />;
    } else {
      return <CaretDown size={18} weight="fill" />;
    }
  }, [isCartExpanded]);

  return (
    <div className={miniCartStyle}>
      <button
        className={styles.miniCartToggle}
        onClick={() => setIsCartExpanded(!isCartExpanded)}
      >
        {`Total: ${totalCost}`}
        {caretDisplay}
      </button>
      <div className={styles.miniCartContent}>
        <RegistrationListing
          registrationTypeLabel={registrationTypeLabel}
          registrationCost={registrationTypeCost}
        />
        <AddOnListings selectedProducts={selectedProducts} />
      </div>
    </div>
  );
}

function RegistrationListing({
  registrationTypeLabel,
  registrationCost,
}: Readonly<{
  registrationTypeLabel: string;
  registrationCost: string;
}>) {
  return (
    <div className={styles.itemListing}>
      <span className={styles.leftColumn}>
        <span className={styles.itemName}>{registrationTypeLabel}</span>
      </span>
      <span className={styles.rightColumn}>
        <span className={styles.itemCost}>{registrationCost}</span>
      </span>
    </div>
  );
}

function AddOnListings({
  selectedProducts,
}: Readonly<{ selectedProducts: AddOnProduct[] }>) {
  return (
    <>
      {selectedProducts.map((product, idx) => (
        <AddOnListing {...product} key={`${product.productName}-${idx}`} />
      ))}
    </>
  );
}

function AddOnListing({
  addOnType,
  itemCost,
  productId,
  productInstanceId,
  productName,
  quantity,
  salesTax,
  salesTaxRequired,
  selectionTypes,
  taxRate,
}: Readonly<AddOnProduct>) {
  const selectUserSelectionValuesByProduct = useMemo(
    () => makeSelectUserSelectionValuesByProduct(productId, selectionTypes),
    [productId, selectionTypes]
  );
  const userSelectionValues = useSelector(selectUserSelectionValuesByProduct);
  return (
    <div className={styles.itemListing}>
      <span className={styles.leftColumn}>
        <span className={styles.itemName}>{productName}</span>
        <span className={styles.selectedOptions}>{userSelectionValues}</span>
        <TaxInformation
          itemCost={itemCost}
          salesTax={salesTax}
          salesTaxRequired={salesTaxRequired}
          taxRate={taxRate}
        />
        {addOnType === 'optional' ? (
          <span className={styles.actionsRow}>
            <QuantityAdjuster
              productInstanceId={productInstanceId}
              quantity={quantity}
            />
            <DeleteButton productInstanceId={productInstanceId} />
          </span>
        ) : null}
      </span>
      <span className={styles.rightColumn}>
        <span className={styles.itemCost}>{formatAsUSD(Number(itemCost))}</span>
      </span>
    </div>
  );
}

function TaxInformation({
  itemCost,
  salesTax,
  salesTaxRequired,
  taxRate,
}: Readonly<{
  itemCost: number;
  salesTax: number;
  salesTaxRequired: boolean;
  taxRate: number;
}>) {
  if (!salesTaxRequired || itemCost === 0) {
    return null;
  }

  return (
    <span className={styles.taxInformation}>
      {`Tax: + ${formatAsUSD(salesTax)} (${taxRate}%)`}
    </span>
  );
}

function QuantityAdjuster({
  productInstanceId = 'productInstanceId-not-set-yet',
  quantity,
}: Readonly<{ productInstanceId?: string; quantity: number }>) {
  const { updateAddOnQuantity } = useRegistrationAddOns();

  return (
    <NumberInput
      id={`quantityAdjuster-${productInstanceId}`}
      min={1}
      onChangeValue={(value) => {
        updateAddOnQuantity(productInstanceId, value);
      }}
      size="medium"
      value={quantity}
      width="80px"
    />
  );
}

function DeleteButton({
  productInstanceId,
}: Readonly<{
  productInstanceId?: string;
}>) {
  const { deleteOptionalAddOn } = useRegistrationAddOns();

  function handleDelete() {
    if (productInstanceId) {
      deleteOptionalAddOn(productInstanceId);
    }
  }

  return (
    <button className={styles.deleteButton} onClick={handleDelete}>
      <Trash color="var(--secondary-600)" size={20} weight="bold" />
    </button>
  );
}
